import { coreCareerApi } from '@cp/entities/career';
import { Box, BoxProps } from '@cp/ds/src/components/box';
import { Tag } from '@cp/ds/src/components/tag';
import { Typography } from '@cp/ds/src/components/typography';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import capitalize from 'lodash/capitalize';
import React from 'react';
import { StyledUnorderedList } from '@cp/ds/src/components/styled/styledUnorderedList';
import { StyledListItem } from '@cp/ds/src/components/styled/styledListItem';
import { TAG_LIST_COLLAPSED_COUNT } from '@cp/ds/src/components/tag/const';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { getCityRegionId } from '@common/utils/region';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { CATALOG_MIN_ITEMS_COUNT_FOR_INDEXING } from '@common/const/catalog';
import { useCurrentCity } from '@cp/entities/location';
import {
  CITY_PATH_PARAMETER,
  coreRouterApi,
  useClientWebResolver,
  useClientWebRouteParams,
  useClientWebRouteProps,
} from '@cp/entities/clientWebRoutes';
import { useRouter } from '@cp/shared/router';
import { Link } from '@cp/ds/src/components/link';
import { coreCatalogApi } from '@cp/entities/catalog';

export interface CatalogLocationLinkingProps extends BoxProps {}

export const CatalogLocationLinking: React.FC<CatalogLocationLinkingProps> = ({ sx }) => {
  const theme = useTheme();
  const pageProps = useClientWebRouteProps<ClientWebPage.Catalog>();
  const { careerId, catalogType, filter } = pageProps;
  const { currentCity } = useCurrentCity();
  const resolver = useClientWebResolver();
  const router = useRouter();
  const { routeId } = useClientWebRouteParams();
  const { data: careerDictionary } = coreCareerApi.endpoints.getCareers.useQuery();
  const { data: routeMeta } = coreRouterApi.endpoints.getClientWebRouteMeta.useQuery({ routeId: Number(routeId) }, { skip: !routeId });
  const cityRegionId = getCityRegionId(currentCity);

  const { data: catalogItemsBatchedCount, isLoading } = coreCatalogApi.endpoints.getItemsCountingForCityLinking.useQuery(
    {
      cityId: currentCity.id,
      cityRegionId: cityRegionId as number,
      type: catalogType,
      careerId,
      filter,
    },
    { skip: !cityRegionId },
  );

  const [isExpanded, setExpanded] = React.useState(false);

  const careerNamePlural = routeMeta?.breadcrumb ?? careerDictionary?.[careerId].translation.namePlural;
  const title = careerNamePlural ? `${capitalize(careerNamePlural)} в вашем регионе` : '';

  return isLoading ? (
    <ContentSkeleton blocks={3} />
  ) : !catalogItemsBatchedCount ? null : (
    <Box
      sx={{
        p: 3.75,
        border: `1px solid ${theme.palette['brand gray']}`,
        borderRadius: `${theme.borderRadius.extraLarge}px`,
        backgroundColor: 'white',
        ...sx,
      }}
    >
      <Typography component="h3" sx={{ mt: 0, mb: 1.5 }} variant="h6 Bold">
        {title}
      </Typography>
      <StyledUnorderedList sx={{ mx: -0.75, display: 'flex', flexWrap: 'wrap' }}>
        {catalogItemsBatchedCount.map(({ hidden, cityId, city, totalCount }, index) => {
          const href = resolver.resolve({
            page: ClientWebPage.Catalog,
            pageProps,
            pathParams: { [CITY_PATH_PARAMETER]: city.alias },
          });
          return (
            <StyledListItem key={cityId} sx={{ my: 1, mx: 0.75, display: index >= 4 && !isExpanded ? 'none' : undefined }}>
              <Tag href={hidden ? undefined : String(href)} onClick={() => router.navigate(href)}>
                {careerNamePlural ? capitalize(careerNamePlural) : ''} в {city.translation?.namePrepositional}
              </Tag>
            </StyledListItem>
          );
        })}
      </StyledUnorderedList>

      {!isExpanded && catalogItemsBatchedCount.length > 4 && (
        <Link onClick={() => setExpanded(true)} sx={{ mt: 0.75, mx: 0.75, display: 'block' }}>
          Показать ещё
        </Link>
      )}
    </Box>
  );
};
